import { inject, injectable } from 'inversify'
import TYPES from 'config/inversify.types'
import { makeAutoObservable } from 'mobx'
import { Dashboard as DashboardDto, DashboardCreate } from '@kibsi/ks-tenant-types'
import type { DashboardService } from 'service/dashboard/dashboard.service'
import type { HistoryService } from 'service/history'
import type { EventService } from 'service/event'
import { DeploymentStore } from 'store/deployment'
import { StreamStore } from 'store/stream'
import { Dashboard } from './dashboard'
import { AsyncDomainStore, AsyncFromDtoDomainStore } from '../domain'
import { FloorPlanStore } from '../floor-plan'

@injectable()
export class DashboardStore {
    private dashboards: AsyncDomainStore<Dashboard, DashboardDto>

    constructor(
        @inject(TYPES.DashboardService) private service: DashboardService,
        @inject(TYPES.FloorPlanStore) private floorPlanStore: FloorPlanStore,
        @inject(TYPES.HistoryService) private historyService: HistoryService,
        @inject(TYPES.DeploymentStore) private deploymentStore: DeploymentStore,
        @inject(TYPES.EventService) private eventService: EventService,
        @inject(TYPES.StreamStore) private streamStore: StreamStore,
    ) {
        this.dashboards = new AsyncFromDtoDomainStore<DashboardDto, Dashboard>(
            (id) => service.get(id),
            (_, data) =>
                new Dashboard(
                    data,
                    service,
                    this.floorPlanStore,
                    this.historyService,
                    this.deploymentStore,
                    this.eventService,
                    this.streamStore,
                ),
        )

        makeAutoObservable<DashboardStore, 'service'>(this, {
            service: false,
        })
    }

    async list(siteId: string): Promise<Dashboard[]> {
        const dtos = await this.service.list(siteId, { pageSize: 100 })
        return this.dashboards.setAll(dtos.map((d) => [d.dashboardId, d]))
    }

    getDashboard(id: string): Promise<Dashboard> {
        return this.dashboards.load(id)
    }

    async createDashboard(dashboard: DashboardCreate): Promise<Dashboard> {
        const dto = await this.service.create(dashboard)

        return this.dashboards.set(dto.dashboardId, dto)
    }

    async deleteDashboard(dashboardId: string, cascade?: boolean): Promise<void> {
        await this.service.delete(dashboardId, cascade)
        this.dashboards.delete(dashboardId)
    }
}
